import { CAVE_SLAB, inSilhouette, POI_LAYOUTS, POI_LOOKS, type PoiLookPiece, type PoiPiece, type PoiType } from "@eldrun/content";
import { BoxGeometry, BufferAttribute, BufferGeometry, ConeGeometry, CylinderGeometry, DodecahedronGeometry, Euler, Matrix4, Quaternion, Vector3 } from "three";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";
import { withCrownNormals } from "./geometry/crown-normals.ts";
import { POI_PIECE_MODELS } from "../assets/models.generated.ts";
import { lumpy, merge, type Piece } from "./mesh-kit.ts";
import type { ModelLibrary } from "./model-library.ts";

const WOOD = "#8B5A2B";
const DARK = "#5C3A20";
const CLOTH = "#7A6448";
const STONE = "#8A8578";
const STONE_DARK = "#6E6A60";
const ROCK = "#7D776C";
/** Pedra cinza das paredes do Dungeon pack e o topo escuro delas. */
const WALL = "#8E949A";
const CAP = "#4A4E54";
const BARREL = "#A0522D";
const HOOP = "#9AA0A6";

/** Parede de pedra com o topo escuro, `w` de largura (a reserva das paredes do Dungeon pack). */
const wall = (w: number): Piece[] => [
  { geometry: new BoxGeometry(w, 2.3, 0.6), color: WALL, position: [0, 1.15, 0] },
  { geometry: new BoxGeometry(w, 0.3, 0.65), color: CAP, position: [0, 2.45, 0] },
];

/**
 * Pedra deformada (`lumpy`) com o ponto mais baixo em y = `base` (o pé do modelo, afundado `sink`),
 * medido na malha já deformada: o raio nominal não serve, a deformação também mexe na altura. `top`
 * é a altura do ponto mais alto, para apoiar outra pedra em cima.
 */
function rock(radius: number, amount: number, seed: number, color: string, scale: readonly [number, number, number], base: number): Piece & { top: number } {
  const geometry = lumpy(new DodecahedronGeometry(radius, 0), amount, seed);
  geometry.computeBoundingBox();
  const { min, max } = geometry.boundingBox!;
  return { geometry, color, scale, position: [0, base - min.y * scale[1], 0], top: base + (max.y - min.y) * scale[1] };
}

/**
 * Reserva da laje do teto da Caverna: as caixas do colisor dela (`POI_LAYOUTS.gruta`), no espaço da peça
 * (`CAVE_SLAB`, a mesma nas duas montagens), de fundo plano e espessuras desiguais (0,9 a 1,1 m: o pé, no
 * colisor a 0,8 m, afunda no máximo 0,3 m). O contorno é o do colisor: sem o pacote de modelos, a cabeça
 * não bate onde não há rocha, e onde há rocha sobre o olho ela bate.
 */
function caveSlab(): Piece[] {
  const { x: px, z: pz } = pieceAt(CAVE_SLAB);
  const [c, s, scale] = [Math.cos(CAVE_SLAB.rot), Math.sin(CAVE_SLAB.rot), CAVE_SLAB.scale ?? 1];
  return POI_LAYOUTS.gruta.props.flatMap((p, i): Piece[] => {
    const k = p.collider;
    if (k?.kind !== "caixa" || k.y === undefined || "a" in p) return [];
    // Do POI à peça: tira a posição dela e desfaz o giro e a escala.
    const [dx, dz] = [p.x - px, p.z - pz];
    const h = 1.1 - (i % 3) * 0.1;
    return [{ geometry: new BoxGeometry(k.w / scale, h, k.d / scale), color: STONE_DARK, position: [(dx * c - dz * s) / scale, h / 2, (dx * s + dz * c) / scale], rotation: [0, -CAVE_SLAB.rot, 0] }];
  });
}

/**
 * Contorno do arco da garganta da Caverna (meia-largura e altura 1), irregular como a rocha: da
 * quina direita do chão, no sentido anti-horário visto de frente, e o chão de volta com 2 pontos.
 */
const ARCH = [[1, 0], [1, 0.26], [0.97, 0.53], [0.83, 0.77], [0.52, 0.94], [0.06, 1], [-0.44, 0.95], [-0.79, 0.78], [-0.97, 0.54], [-0.99, 0.27], [-1, 0], [-0.38, 0], [0.36, 0]] as const;

/**
 * Anéis da garganta, da boca ao fundo (z local, meia-largura e altura em m), com a sombra que
 * multiplica a cor da peça. A boca, larga e alta, fica dentro dos pilares e da laje: o contorno que se
 * vê é o da rocha. No alto ela recua (`crown`, os pontos do arco acima de 90 %): o teto fica atrás da
 * coluna onde o olho do jogador chega, parado no colisor do fundo, mesmo no pulo mais alto. Daí a
 * garganta afunila até o arco do fundo, quase preto.
 */
const THROAT: readonly { z: number; crown?: number; w: number; h: number; shade: number }[] = [
  { z: 0.375, crown: 0, w: 2.8, h: 4.4, shade: 1 },
  { z: -0.17, w: 2.1, h: 3.5, shade: 0.5 },
  { z: -0.375, w: 1.45, h: 2.75, shade: 0.16 },
];

/** Desvio fixo em [−0,5; 0,5) para o vértice `k` (o mesmo em toda carga). */
const wobble = (k: number) => {
  const h = Math.sin(k * 12.9898) * 43758.5453;
  return h - Math.floor(h) - 0.5;
};

/**
 * Garganta da Caverna (`boca-sombra`): o túnel que entra na rocha, de paredes viradas para dentro (de
 * fora não aparecem) e o fundo tampado, a cor escurecendo com a profundidade. O chão fica rente ao do
 * POI; o anel do meio sai do alinhamento para a parede parecer rocha. O verso do fundo, cor de rocha,
 * fecha o vão do penhasco para quem olha por trás.
 */
function throat(): { inside: BufferGeometry; back: BufferGeometry } {
  const rings = THROAT.map(({ z, crown, w, h }, k) =>
    ARCH.map(([u, v], i): [number, number, number] => {
      const d = k === 1 ? 0.24 : 0;
      return [u * w + wobble(i + 1) * d, v === 0 ? 0.03 : 0.03 + v * (h - 0.03) + wobble(i + 17) * d, (v > 0.9 ? (crown ?? z) : z) + wobble(i + 33) * d * 0.5];
    }),
  );
  const [back, deep] = [rings[2]!, THROAT[2]!];
  const center = [0, 0.4 * deep.h, deep.z];
  const inside: number[] = [];
  const shade: number[] = [];
  /** Triângulo de dentro: os vértices na ordem e a sombra de cada um. */
  const tri = (...corners: [readonly number[], number][]) => {
    for (const [p, s] of corners) {
      inside.push(...p);
      shade.push(s, s, s);
    }
  };
  const n = ARCH.length;
  for (let k = 0; k < 2; k++) {
    const [a, b] = [rings[k]!, rings[k + 1]!];
    const [sa, sb] = [THROAT[k]!.shade, THROAT[k + 1]!.shade];
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      // Anel anti-horário visto de frente e o seguinte mais fundo: a face da frente olha para dentro.
      tri([a[i]!, sa], [a[j]!, sa], [b[j]!, sb]);
      tri([a[i]!, sa], [b[j]!, sb], [b[i]!, sb]);
    }
  }
  const verso: number[] = [];
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    tri([center, 0.08], [back[i]!, deep.shade], [back[j]!, deep.shade]);
    verso.push(...center, ...back[j]!, ...back[i]!);
  }
  const g = new BufferGeometry();
  g.setAttribute("position", new BufferAttribute(new Float32Array(inside), 3));
  g.setAttribute("color", new BufferAttribute(new Float32Array(shade), 3));
  const v = new BufferGeometry();
  v.setAttribute("position", new BufferAttribute(new Float32Array(verso), 3));
  return { inside: g, back: v };
}

/** Barril de pé (raio `r`, altura `h`) com dois aros. */
const barrel = (r: number, h: number): Piece[] => [
  { geometry: new CylinderGeometry(r * 0.9, r * 0.9, h, 10), color: BARREL, position: [0, h / 2, 0] },
  ...[0.28, 0.72].map((t): Piece => ({ geometry: new CylinderGeometry(r, r, h * 0.09, 10), color: HOOP, position: [0, h * t, 0] })),
];

/**
 * Peças das montagens dos POIs (doc 04 e F43; ADR 0007): as que só existem aqui (fogueira, barracas,
 * carroça, varal, casco e mastro do Naufrágio, pedras do santuário, garganta da Caverna) e a reserva
 * procedural das que têm modelo no pipeline, nas medidas de `POI_PIECES`. Base em y = 0, frente
 * para +z (a montagem gira).
 */
const PROPS: Record<PoiPiece, () => Piece[]> = {
  fogueira: () => [
    ...[0, 1, 2, 3, 4].map((i): Piece => ({ geometry: new CylinderGeometry(0.08, 0.08, 1, 5), color: DARK, position: [0, 0.18, 0], rotation: [Math.PI / 2.4, (i / 5) * Math.PI * 2, 0] })),
    { geometry: new ConeGeometry(0.35, 0.8, 6), color: "#F28A2E", position: [0, 0.45, 0] },
    { geometry: new CylinderGeometry(0.75, 0.8, 0.12, 9), color: STONE, position: [0, 0.06, 0] },
  ],
  "fogueira-apagada": () => [
    ...[0, 1, 2, 3].map((i): Piece => ({ geometry: new CylinderGeometry(0.07, 0.07, 0.9, 5), color: "#3A2A1C", position: [0, 0.1, 0], rotation: [Math.PI / 2, (i / 4) * Math.PI * 2, 0] })),
    { geometry: new CylinderGeometry(0.6, 0.65, 0.1, 8), color: STONE_DARK, position: [0, 0.05, 0] },
  ],
  barraca: () => [
    { geometry: new ConeGeometry(1.7, 2.4, 4), color: CLOTH, position: [0, 1.2, 0], rotation: [0, Math.PI / 4, 0] },
    { geometry: new BoxGeometry(0.7, 1.4, 0.05), color: "#4A3B2A", position: [0, 0.7, 1.15] },
  ],
  // Caçamba de 2,2 × 1,3 m sobre quatro rodas; o varal apoiado no chão cabe no colisor.
  carroca: () => [
    { geometry: new BoxGeometry(2.2, 0.5, 1.3), color: WOOD, position: [0, 0.9, 0] },
    { geometry: new BoxGeometry(2.2, 0.4, 0.08), color: DARK, position: [0, 1.3, 0.62] },
    { geometry: new BoxGeometry(2.2, 0.4, 0.08), color: DARK, position: [0, 1.3, -0.62] },
    ...[-0.8, 0.8].map((x): Piece => ({ geometry: new CylinderGeometry(0.5, 0.5, 0.1, 10), color: DARK, position: [x, 0.5, 0.72], rotation: [Math.PI / 2, 0, 0] })),
    ...[-0.8, 0.8].map((x): Piece => ({ geometry: new CylinderGeometry(0.5, 0.5, 0.1, 10), color: DARK, position: [x, 0.5, -0.72], rotation: [Math.PI / 2, 0, 0] })),
    ...[-0.4, 0.4].map((z): Piece => ({ geometry: new BoxGeometry(0.75, 0.08, 0.08), color: DARK, position: [1.35, 0.42, z], rotation: [0, 0, -0.85] })),
  ],
  varal: () => [
    ...[-1.5, 1.5].map((x): Piece => ({ geometry: new CylinderGeometry(0.06, 0.06, 1.8, 5), color: DARK, position: [x, 0.9, 0] })),
    { geometry: new BoxGeometry(3, 0.02, 0.02), color: "#D9D2C0", position: [0, 1.7, 0] },
    { geometry: new BoxGeometry(1.1, 0.7, 0.04), color: "#C2483A", position: [-0.6, 1.3, 0] },
    { geometry: new BoxGeometry(0.9, 0.6, 0.04), color: "#3E6FA8", position: [0.7, 1.35, 0] },
  ],
  "tronco-banco": () => [{ geometry: new CylinderGeometry(0.3, 0.32, 1.6, 7), color: WOOD, position: [0, 0.3, 0], rotation: [0, 0, Math.PI / 2] }],
  // Casco emborcado com as cavernas (costelas) para cima, da altura do colisor.
  "destrocos-casco": () => [
    ...[-1.2, -0.4, 0.4, 1.2].map((x, i): Piece => ({ geometry: new BoxGeometry(0.7, 0.18, 4 - i * 0.4), color: i % 2 ? "#6E5235" : "#7E6040", position: [x, 0.25 + i * 0.03, 0], rotation: [0, 0, 0.05 * (i - 1.5)] })),
    ...[-1.5, 0, 1.5].map((z): Piece => ({ geometry: new CylinderGeometry(0.16, 0.16, 3.2, 6), color: DARK, position: [0, 0.4, z], rotation: [0, 0, Math.PI / 2] })),
    ...[-1.3, 0, 1.3].flatMap((z) => [-1, 1].map((side): Piece => ({ geometry: new BoxGeometry(0.14, 1.1, 0.18), color: DARK, position: [side * 1.45, 0.55, z], rotation: [0, 0, side * -0.22] }))),
  ],
  // Mastro e vela caídos no chão.
  "destrocos-mastro": () => [
    { geometry: new CylinderGeometry(0.12, 0.15, 4, 6), color: WOOD, position: [0, 0.13, 0], rotation: [0, 0, Math.PI / 2] },
    { geometry: new BoxGeometry(1.8, 0.04, 1.4), color: "#D8CDB0", position: [0.9, 0.02, 0.6], rotation: [0, 0.4, 0] },
  ],
  "santuario-pedra": () => [{ geometry: lumpy(new DodecahedronGeometry(0.75, 0), 0.12, 13), color: STONE, position: [0, 1.3, 0], scale: [0.8, 1.9, 0.7] }],
  "boca-sombra": () => {
    const { inside, back } = throat();
    return [
      { geometry: inside, color: "#615C54" },
      { geometry: back, color: STONE_DARK },
    ];
  },
  // Reservas das peças com modelo.
  parede: () => wall(2.6),
  "parede-meia": () => wall(1.3),
  pilar: () => [
    { geometry: new CylinderGeometry(0.6, 0.65, 3.6, 8), color: WALL, position: [0, 1.8, 0] },
    { geometry: new CylinderGeometry(0.75, 0.7, 0.4, 8), color: CAP, position: [0, 3.8, 0] },
  ],
  "pilar-caido": () => [
    { geometry: new CylinderGeometry(0.49, 0.49, 2.1, 8), color: WALL, position: [-0.3, 0.49, 0], rotation: [0, 0, Math.PI / 2] },
    { geometry: new BoxGeometry(0.55, 0.4, 0.9), color: WALL, position: [1.05, 0.2, 0] },
    { geometry: new BoxGeometry(0.55, 0.4, 0.9), color: WALL, position: [1.05, 0.6, 0], rotation: [0, 0.15, 0] },
    { geometry: new BoxGeometry(0.55, 0.38, 0.8), color: WALL, position: [0.3, 0.19, 0.8], rotation: [0, 0.4, 0] },
    { geometry: new BoxGeometry(0.55, 0.38, 0.8), color: WALL, position: [-0.7, 0.19, -0.8], rotation: [0, 2.6, 0] },
  ],
  entulho: () => [
    { geometry: lumpy(new DodecahedronGeometry(0.3, 0), 0.25, 3), color: STONE, position: [-0.2, 0.22, 0], scale: [1, 0.8, 1] },
    { geometry: lumpy(new DodecahedronGeometry(0.25, 0), 0.25, 5), color: STONE_DARK, position: [0.24, 0.18, 0.1], scale: [1, 0.8, 1] },
    { geometry: lumpy(new DodecahedronGeometry(0.2, 0), 0.25, 7), color: STONE, position: [0.05, 0.14, -0.18], scale: [1, 0.8, 1] },
  ],
  tijolos: () => [
    ...[-0.18, 0.18].map((x): Piece => ({ geometry: new BoxGeometry(0.34, 0.22, 0.6), color: WALL, position: [x, 0.11, 0] })),
    { geometry: new BoxGeometry(0.6, 0.22, 0.34), color: WALL, position: [0.05, 0.33, 0] },
  ],
  laje: () => [{ geometry: new BoxGeometry(1.33, 0.1, 1.33), color: "#A89A8A", position: [0, 0.01, 0] }],
  barril: () => barrel(0.44, 0.9),
  "barril-deitado": () => barrel(0.3, 0.6).map((p) => ({ ...p, position: [p.position![1] - 0.3, 0.3, 0] as const, rotation: [0, 0, Math.PI / 2] as const })),
  caixote: () => [
    { geometry: new BoxGeometry(0.8, 0.8, 0.8), color: "#8A5A32", position: [0, 0.4, 0] },
    { geometry: new BoxGeometry(0.82, 0.1, 0.82), color: HOOP, position: [0, 0.75, 0] },
  ],
  barrica: () => [
    { geometry: new CylinderGeometry(0.42, 0.42, 0.85, 10), color: BARREL, position: [0, 0.56, 0], rotation: [Math.PI / 2, 0, 0] },
    { geometry: new BoxGeometry(0.7, 0.16, 0.8), color: DARK, position: [0, 0.08, 0] },
  ],
  banco: () => [
    { geometry: new BoxGeometry(0.75, 0.12, 0.75), color: BARREL, position: [0, 0.44, 0] },
    ...[-1, 1].flatMap((x) => [-1, 1].map((z): Piece => ({ geometry: new BoxGeometry(0.12, 0.38, 0.12), color: BARREL, position: [x * 0.27, 0.19, z * 0.27] }))),
  ],
  mesa: () => [
    { geometry: new BoxGeometry(1.4, 0.1, 0.8), color: BARREL, position: [0, 0.32, -0.3], rotation: [0, 0, 0.4] },
    ...[-0.55, 0.55].map((x): Piece => ({ geometry: new BoxGeometry(0.1, 0.5, 0.1), color: DARK, position: [x, 0.25, -0.55] })),
    { geometry: new BoxGeometry(0.3, 0.06, 1.2), color: BARREL, position: [0.3, 0.03, 0.15], rotation: [0, 0.5, 0] },
  ],
  tocha: () => [
    { geometry: new CylinderGeometry(0.04, 0.05, 1.4, 5), color: DARK, position: [0, 0.7, 0] },
    { geometry: new CylinderGeometry(0.14, 0.11, 0.24, 6), color: "#2A2622", position: [0, 1.48, 0] },
  ],
  tabuas: () => [0, 1, 2].map((i): Piece => ({ geometry: new BoxGeometry(1.6 - i * 0.3, 0.06, 0.3), color: "#C9A26E", position: [0, 0.04 + i * 0.01, -0.45 + i * 0.45], rotation: [0, i * 0.3, 0] })),
  lenha: () => [
    ...[-0.24, 0.24].map((x): Piece => ({ geometry: new CylinderGeometry(0.24, 0.24, 1.1, 7), color: WOOD, position: [x, 0.24, 0], rotation: [Math.PI / 2, 0, 0] })),
    { geometry: new CylinderGeometry(0.22, 0.22, 1.15, 7), color: DARK, position: [0, 0.38, 0], rotation: [Math.PI / 2, 0.1, 0] },
  ],
  // Pedras com o pé do modelo (`sink` do manifesto); no pilar, a de cima apoiada na de baixo.
  "pilar-rocha": () => {
    const lower = rock(1.25, 0.2, 3, ROCK, [1, 1, 1.1], -0.1);
    return [lower, rock(1.05, 0.2, 5, STONE_DARK, [1, 1.15, 1], lower.top - 0.15)];
  },
  penhasco: () => [rock(2.8, 0.25, 7, ROCK, [0.82, 0.75, 0.92], -0.1)],
  "laje-rocha": caveSlab,
  pedregulho: () => [rock(0.8, 0.2, 11, ROCK, [1.1, 0.8, 1], -0.1)],
  "pedra-solta": () => [rock(0.38, 0.2, 13, ROCK, [1.08, 0.6, 0.9], -0.05)],
};

const cache = new Map<PoiPiece, BufferGeometry>();

/** Malha procedural da peça (cacheada): a única das peças sem modelo, a reserva das outras. */
export function pieceGeometry(piece: PoiPiece): BufferGeometry {
  let g = cache.get(piece);
  if (!g) {
    g = merge(PROPS[piece]());
    cache.set(piece, g);
  }
  return g;
}

export const PIECE_IDS = Object.keys(PROPS) as PoiPiece[];

/** Cópia sem índices com posição, cor e vento em float (o modelo vem com cor em uint16 e vento em uint8). */
function plain(source: BufferGeometry): BufferGeometry {
  const index = source.index;
  const count = index ? index.count : source.getAttribute("position").count;
  const out = new BufferGeometry();
  for (const [name, size] of [["position", 3], ["color", 3], ["wind", 1]] as const) {
    const attribute = source.getAttribute(name);
    const array = new Float32Array(count * size);
    for (let i = 0; i < count; i++) {
      const v = index ? index.getX(i) : i;
      for (let k = 0; k < size; k++) array[i * size + k] = attribute.getComponent(v, k);
    }
    out.setAttribute(name, new BufferAttribute(array, size));
  }
  return out;
}

/** Peça no LOD: o modelo do pipeline (ou o de perto, se faltar o de longe), senão a procedural. */
function pieceSource(piece: PoiPiece, lod: 0 | 1, library: ModelLibrary): BufferGeometry {
  const model = POI_PIECE_MODELS[piece];
  return (model && (library.get(model, lod) ?? library.get(model, 0))) || pieceGeometry(piece);
}

const m4 = new Matrix4();
const q = new Quaternion();
const e = new Euler();
const v = new Vector3();
const s = new Vector3();

/** Posição local da peça (âncora cartesiana ou polar, como no layout). */
export function pieceAt(p: PoiLookPiece): { x: number; z: number } {
  return "a" in p ? { x: Math.sin(p.a) * p.r, z: Math.cos(p.a) * p.r } : { x: p.x, z: p.z };
}

/**
 * Montagem `look` do tipo numa geometria só, no espaço do POI (F43): de perto (`lod` 0) todas as
 * peças, de longe (1) só as da silhueta, cada uma com o modelo ou a reserva procedural. Sem índice
 * (F59: a malha neon precisa dos 3 vértices próprios de cada triângulo; antes era soldada de novo).
 * `undefined` se o LOD não tem peça (a Chegada não tem silhueta).
 */
export function poiLookGeometry(type: PoiType, look: number, lod: 0 | 1, library: ModelLibrary): BufferGeometry | undefined {
  const pieces = (POI_LOOKS[type]?.[look] ?? []).filter((p) => lod === 0 || inSilhouette(p));
  if (pieces.length === 0) return undefined;
  const parts = pieces.map((p) => {
    const g = plain(pieceSource(p.piece, lod, library));
    const { x, z } = pieceAt(p);
    const scale = p.scale ?? 1;
    return g.applyMatrix4(m4.compose(v.set(x, p.y ?? 0, z), q.setFromEuler(e.set(0, p.rot, 0)), s.set(scale, scale, scale)));
  });
  const merged = mergeGeometries(parts, false);
  for (const g of parts) g.dispose();
  if (!merged) throw new Error(`montagem ${type}[${look}]: falha ao fundir as peças`);
  merged.computeBoundingSphere();
  // Fase 14: as copas, a normal de moita.
  return withCrownNormals(merged);
}
